import { useMemo, useState } from 'react';
import { useToast } from '@/components/common/toast';
import { FilterBar } from '@/components/common/FilterBar';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { AsyncButton } from '@/components/common/AsyncButton';
import { Modal } from '@/components/dialogs/Modal';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { ApiError } from '@/api/client/abpError';
import { swapFreeControlApi, fetchConfigurationNames } from '@/api/services/swapFreeControl';
import {
  ConfigurationType,
  type GetConfigurationsInput,
  type SwapFreeConfigurationDto,
  type SwapFreeOptionsDto,
} from '@/api/types/swapFreeControl';
import { ConfigDetailTable } from '../ConfigDetailTable';
import { fmtDateTime, keyOf, toLabelMap } from '../helpers';
import {
  hasChanges,
  newStaging,
  stagingSummary,
  toApplyInput,
  type StagingModel,
} from '../staging';

interface Props {
  options: SwapFreeOptionsDto;
  canEdit: boolean;
  canDelete: boolean;
  canAdd: boolean;
  onGoToAddConfig: () => void;
}

interface EditDraft {
  name: string;
  swapFreeDays: string;
  directionText: string;
  calculationTypeText: string;
}

export function ConfigurationsTab({ options, canEdit, canDelete, canAdd, onGoToAddConfig }: Props) {
  const toast = useToast();
  const directionsMap = useMemo(() => toLabelMap(options.directions), [options.directions]);
  const calcMap = useMemo(() => toLabelMap(options.calculationTypes), [options.calculationTypes]);

  // Filters.
  const [nameFilter, setNameFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [applied, setApplied] = useState<{ name: string | null; status: string; type: string }>({ name: null, status: '', type: '' });

  // Inline edit + staging (single-row invariant).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [staging, setStaging] = useState<StagingModel | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  // Single-open detail invariant.
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Dialog state.
  const [statusTarget, setStatusTarget] = useState<SwapFreeConfigurationDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SwapFreeConfigurationDto | null>(null);
  const [saveTarget, setSaveTarget] = useState<SwapFreeConfigurationDto | null>(null);
  const [discardThen, setDiscardThen] = useState<null | (() => void)>(null);

  const extra = useMemo<Partial<GetConfigurationsInput>>(
    () => ({
      name: applied.name || undefined,
      status: applied.status === '' ? undefined : Number(applied.status),
      type: applied.type === '' ? undefined : Number(applied.type),
    }),
    [applied],
  );
  const table = useServerDataTable<SwapFreeConfigurationDto, Partial<GetConfigurationsInput>>({
    queryKey: ['swap-free', 'configurations'],
    fetcher: (req) => swapFreeControlApi.getConfigurations(req as GetConfigurationsInput),
    pageSize: 5,
    extraParams: extra,
  });

  const softReload = () => void table.refetch();

  const applyFilters = () => {
    table.setPageIndex(0);
    setApplied({ name: nameFilter, status: statusFilter, type: typeFilter });
  };

  // --- Inline edit lifecycle ---------------------------------------------------
  const beginEdit = (row: SwapFreeConfigurationDto) => {
    setEditingId(row.id);
    setDraft({
      name: row.name ?? '',
      swapFreeDays: String(row.swapFreeDays),
      directionText: row.directionText,
      calculationTypeText: row.calculationTypeText,
    });
    setStaging(newStaging(row.id, row.type === ConfigurationType.Groups));
    setExpandedId(row.id); // auto-expand detail
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
    setStaging(null);
  };

  const guardDiscard = (action: () => void) => {
    if (staging && hasChanges(staging)) {
      setDiscardThen(() => action);
    } else {
      action();
    }
  };

  const toggleExpand = (row: SwapFreeConfigurationDto) => {
    if (expandedId === row.id) {
      // Collapsing the edited row with unsaved staging -> guard.
      if (editingId === row.id) {
        guardDiscard(() => {
          cancelEdit();
          setExpandedId(null);
        });
      } else {
        setExpandedId(null);
      }
    } else {
      // Opening a different row while editing -> guard discard of current edit.
      if (editingId && editingId !== row.id) {
        guardDiscard(() => {
          cancelEdit();
          setExpandedId(row.id);
        });
      } else {
        setExpandedId(row.id);
      }
    }
  };

  const validateDraft = (): boolean => {
    if (!draft) return false;
    if (!draft.name.trim()) {
      toast.error('Config Name is required.');
      return false;
    }
    if (!(Number(draft.swapFreeDays) >= 0) || draft.swapFreeDays.trim() === '') {
      toast.error('Swap-Free Days must be zero or greater.');
      return false;
    }
    return true;
  };

  const openSave = (row: SwapFreeConfigurationDto) => {
    if (!validateDraft()) return;
    setSaveTarget(row);
  };

  const doSave = async () => {
    if (!saveTarget || !draft) return;
    const row = saveTarget;
    setSavingId(row.id);
    try {
      await swapFreeControlApi.updateScalars({
        id: row.id,
        name: draft.name.trim(),
        swapFreeDays: Number(draft.swapFreeDays),
        swapDirection: keyOf(directionsMap, draft.directionText, row.direction),
        swapCalculationType: keyOf(calcMap, draft.calculationTypeText, row.calculationType),
      });
      if (staging && hasChanges(staging)) {
        await swapFreeControlApi.applyTargetChanges(toApplyInput(staging));
      }
      setSaveTarget(null);
      cancelEdit();
      softReload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to save configuration.');
    } finally {
      setSavingId(null);
    }
  };

  // --- Status toggle -----------------------------------------------------------
  const doSetStatus = async () => {
    if (!statusTarget) return;
    const row = statusTarget;
    const next = !row.isActive;
    try {
      await swapFreeControlApi.setStatus({ id: row.id, isActive: next });
      toast.success(next ? 'Swap-free activated.' : 'Swap-free deactivated.');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to update status.');
    } finally {
      setStatusTarget(null);
      softReload();
    }
  };

  // --- Delete ------------------------------------------------------------------
  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await swapFreeControlApi.delete({ id: deleteTarget.id });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to delete configuration.');
    } finally {
      setDeleteTarget(null);
      softReload();
    }
  };

  // --- Columns -----------------------------------------------------------------
  const diffRow = (row: SwapFreeConfigurationDto) => {
    if (!draft) return [];
    const out: { field: string; before: string; after: string }[] = [];
    if (draft.name.trim() !== (row.name ?? '')) out.push({ field: 'Config Name', before: row.name ?? '', after: draft.name.trim() });
    if (Number(draft.swapFreeDays) !== row.swapFreeDays) out.push({ field: 'Swap Free Days', before: String(row.swapFreeDays), after: draft.swapFreeDays });
    if (draft.directionText !== row.directionText) out.push({ field: 'Swap Direction', before: row.directionText, after: draft.directionText });
    if (draft.calculationTypeText !== row.calculationTypeText) out.push({ field: 'Calculation Type', before: row.calculationTypeText, after: draft.calculationTypeText });
    return out;
  };

  const columns: DataGridColumn<SwapFreeConfigurationDto>[] = [
    {
      key: 'name',
      title: 'Config Name',
      className: 'text-start fw-semibold',
      value: (r) => r.name ?? '',
      cell: (r) =>
        editingId === r.id && draft ? (
          <input className="form-control form-control-sm" value={draft.name} disabled={savingId === r.id} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        ) : (
          r.name ?? 'N/A'
        ),
    },
    {
      key: 'scope',
      title: 'Type',
      className: 'text-start',
      value: (r) => r.scope,
      cell: (r) => <span className="badge text-bg-light">{r.scope}</span>,
    },
    {
      key: 'swapFreeDays',
      title: 'Swap Free Days',
      className: 'text-end',
      value: (r) => r.swapFreeDays,
      cell: (r) =>
        editingId === r.id && draft ? (
          <input type="number" min="0" className="form-control form-control-sm" value={draft.swapFreeDays} disabled={savingId === r.id} onChange={(e) => setDraft({ ...draft, swapFreeDays: e.target.value })} />
        ) : (
          r.swapFreeDays
        ),
    },
    {
      key: 'directionText',
      title: 'Swap Direction',
      className: 'text-start',
      value: (r) => r.directionText,
      cell: (r) =>
        editingId === r.id && draft ? (
          <select className="form-select form-select-sm" value={draft.directionText} disabled={savingId === r.id} onChange={(e) => setDraft({ ...draft, directionText: e.target.value })}>
            {options.directions.map((d) => (
              <option key={d.value} value={d.text}>{d.text}</option>
            ))}
          </select>
        ) : (
          r.directionText
        ),
    },
    {
      key: 'calculationTypeText',
      title: 'Calculation Type',
      className: 'text-start',
      value: (r) => r.calculationTypeText,
      cell: (r) =>
        editingId === r.id && draft ? (
          <select className="form-select form-select-sm" value={draft.calculationTypeText} disabled={savingId === r.id} onChange={(e) => setDraft({ ...draft, calculationTypeText: e.target.value })}>
            {options.calculationTypes.map((c) => (
              <option key={c.value} value={c.text}>{c.text}</option>
            ))}
          </select>
        ) : (
          r.calculationTypeText
        ),
    },
    {
      key: 'isActive',
      title: 'Swap-Free Status',
      sortable: false,
      className: 'text-center',
      value: (r) => r.statusText,
      cell: (r) => (
        <div className="form-check form-switch d-inline-flex align-items-center gap-2 sfc-status-switch">
          <input
            type="checkbox"
            className="form-check-input"
            role="switch"
            checked={r.isActive}
            disabled={!canEdit}
            onChange={() => {
              // Immediately revert; confirm first.
              setStatusTarget(r);
            }}
          />
          <span className="small">{r.isActive ? 'Active' : 'InActive'}</span>
        </div>
      ),
    },
    {
      key: 'targetCount',
      title: 'Targets',
      className: 'text-center',
      value: (r) => r.targetCount,
      cell: (r) => {
        const preview = r.targetsPreview.slice(0, 3).join(', ');
        const more = r.targetCount - Math.min(3, r.targetsPreview.length);
        const tip = r.targetsPreview.join(', ');
        return (
          <span title={tip}>
            <span className="badge text-bg-primary">{r.targetCount}</span>
            {preview && <span className="ms-1 small text-muted">{preview}{more > 0 ? ` +${more}` : ''}</span>}
          </span>
        );
      },
    },
    {
      key: 'exceptionCount',
      title: 'Exceptions',
      className: 'text-center',
      value: (r) => r.exceptionCount,
      cell: (r) => (r.exceptionCount > 0 ? <span className="badge text-bg-warning">{r.exceptionCount}</span> : '—'),
    },
    { key: 'createdAt', title: 'Created At', className: 'text-start', value: (r) => r.createdAt ?? '', cell: (r) => fmtDateTime(r.createdAt) },
    { key: 'updatedAt', title: 'Updated At', className: 'text-start', value: (r) => r.updatedAt ?? '', cell: (r) => fmtDateTime(r.updatedAt) },
    { key: 'user', title: 'User', className: 'text-start', value: (r) => r.user ?? '', cell: (r) => r.user ?? 'N/A' },
  ];

  return (
    <div>
      <FilterBar
        card={false}
        className="mb-3"
        fields={[
          <div key="name">
            <label className="form-label">Config Name</label>
            <Select2Async
              placeholder="All"
              fetch={fetchConfigurationNames}
              value={nameFilter}
              onChange={(v) => setNameFilter(v)}
            />
          </div>,
          <Select2 key="status" label="Swap-Free Status" allowClear searchable={false} placeholder="All" value={statusFilter} options={options.statuses.map((s) => ({ value: s.value, label: s.text }))} onChange={(v) => setStatusFilter(v ?? '')} />,
          <Select2 key="type" label="Target" allowClear searchable={false} placeholder="All" value={typeFilter} options={options.types.map((t) => ({ value: t.value, label: t.text }))} onChange={(v) => setTypeFilter(v ?? '')} />,
        ]}
        actions={
          <>
            <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" loading={table.isFetching} onClick={applyFilters}>Fetch</AsyncButton>
            {canAdd && <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-plus" onClick={onGoToAddConfig}>Add Config</AsyncButton>}
          </>
        }
      />

      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => r.id}
        card
        emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
        rowClassName={(r) => (editingId === r.id ? 'sfc-detail-editing' : savingId === r.id ? 'sfc-saving' : undefined)}
        expandedKeys={expandedId ? new Set([expandedId]) : new Set()}
        onExpandToggle={toggleExpand}
        expandable={(r) => (
          <ConfigDetailTable
            configId={r.id}
            isGroups={r.type === ConfigurationType.Groups}
            canEdit={canEdit}
            staging={editingId === r.id ? staging : null}
            onStagingChange={setStaging}
            onRequireEdit={() => toast.warning('Press Edit on the row first.')}
          />
        )}
        rowActions={(row) => (
          <div className="d-flex gap-1">
            {canEdit && editingId !== row.id && (
              <button type="button" className="btn btn-sm btn-link p-0" title="Edit" onClick={() => guardDiscard(() => beginEdit(row))}>
                <i className="fa fa-pencil" />
              </button>
            )}
            {editingId === row.id && (
              <>
                <AsyncButton className="btn btn-sm btn-primary" onClick={() => openSave(row)} disabled={savingId === row.id}>Save</AsyncButton>
                <button type="button" className="btn btn-sm btn-light" disabled={savingId === row.id} onClick={() => guardDiscard(cancelEdit)}>Cancel</button>
              </>
            )}
            {canDelete && editingId !== row.id && (
              <button type="button" className="btn btn-sm btn-link text-danger p-0" title="Delete" onClick={() => setDeleteTarget(row)}>
                <i className="fa fa-trash" />
              </button>
            )}
          </div>
        )}
        server={{
          pageIndex: table.pageIndex,
          pageSize: table.pageSize,
          pageCount: table.pageCount,
          totalCount: table.totalCount,
          onPageChange: (p) => guardDiscard(() => { cancelEdit(); setExpandedId(null); table.setPageIndex(p); }),
          onPageSizeChange: (n) => guardDiscard(() => { cancelEdit(); setExpandedId(null); table.setPageSize(n); }),
        }}
      />

      {/* Status confirm */}
      <ConfirmDialog
        show={statusTarget != null}
        title="Are you sure?"
        message={statusTarget ? `${statusTarget.isActive ? 'Deactivate' : 'Activate'} swap-free for '${statusTarget.name ?? ''}'?` : ''}
        confirmLabel={statusTarget ? (statusTarget.isActive ? 'Yes, deactivate' : 'Yes, activate') : 'Confirm'}
        confirmVariant="primary"
        onConfirm={doSetStatus}
        onCancel={() => { const t = statusTarget; setStatusTarget(null); if (t) softReload(); }}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        show={deleteTarget != null}
        title="Are you sure?"
        message={deleteTarget ? `Delete swap-free configuration '${deleteTarget.name ?? ''}'?` : ''}
        confirmLabel="Delete"
        onConfirm={doDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Save confirm (before -> after + staging summary) */}
      <Modal
        show={saveTarget != null}
        title="Save Configuration"
        onClose={() => setSaveTarget(null)}
        footer={
          <>
            <button type="button" className="btn btn-light btn-sm" onClick={() => setSaveTarget(null)}>Keep editing</button>
            <AsyncButton className="btn btn-primary btn-sm" onClick={doSave}>Save</AsyncButton>
          </>
        }
      >
        {saveTarget && draft && (
          <>
            {diffRow(saveTarget).length > 0 ? (
              <table className="table table-sm mb-2">
                <thead><tr><th>Field</th><th>Before</th><th>After</th></tr></thead>
                <tbody>
                  {diffRow(saveTarget).map((d) => (
                    <tr key={d.field}><th>{d.field}</th><td>{d.before}</td><td>{d.after}</td></tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-muted">No scalar changes.</p>
            )}
            <p className="mb-0"><strong>Target Changes:</strong> {staging ? stagingSummary(staging) : 'None'}</p>
          </>
        )}
      </Modal>

      {/* Discard guard */}
      <ConfirmDialog
        show={discardThen != null}
        title="Discard unsaved changes?"
        message="You have unsaved target changes. Discard them?"
        confirmLabel="Discard"
        confirmVariant="danger"
        onConfirm={() => { const fn = discardThen; setDiscardThen(null); fn?.(); }}
        onCancel={() => setDiscardThen(null)}
      />
    </div>
  );
}
